import type { Campaign, CampaignItem, CampaignItemPipelineStage, Disparo } from '../types';

/** Igual a `DISPARO_CARD_IN_EXEC_COLUMN` no Kanban de campanhas: fila que ocupa a coluna «Em execução». */
const DISPARO_CARD_IN_EXEC_COLUMN: Disparo['status'][] = [
  'em_execucao',
  'pausado',
  'pausado_horario',
  'max_atingido',
];

const CAMPAIGN_TERMINAL_STAGES: CampaignItemPipelineStage[] = ['sucesso', 'perdido', 'encerrado'];

const DISPARO_STATUSES_ACTIVE: Disparo['status'][] = [
  'criado',
  'em_execucao',
  'pausado',
  'pausado_horario',
  'max_atingido',
];

export function disparoInKanbanExecColumn(d: Disparo | undefined | null): boolean {
  return !!d && DISPARO_CARD_IN_EXEC_COLUMN.includes(d.status);
}

export function pickPrimaryDisparoForCampaign(disparos: Disparo[]): Disparo | null {
  if (!disparos.length) return null;
  const byUpdated = [...disparos].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  const active = byUpdated.find((d) => DISPARO_STATUSES_ACTIVE.includes(d.status));
  return active || byUpdated[0];
}

export function buildPrimaryDisparoByCampaignMap(disparos: Disparo[]): Map<string, Disparo> {
  const byCampaign = new Map<string, Disparo[]>();
  for (const d of disparos) {
    const cid = d.campaignId;
    if (!byCampaign.has(cid)) byCampaign.set(cid, []);
    byCampaign.get(cid)!.push(d);
  }
  const out = new Map<string, Disparo>();
  byCampaign.forEach((arr, cid) => {
    const primary = pickPrimaryDisparoForCampaign(arr);
    if (primary) out.set(cid, primary);
  });
  return out;
}

export type CampaignListDisplayKind =
  | 'rascunho'
  | 'encerrada'
  | 'pendente'
  | 'em_execucao'
  | 'finalizada';

/**
 * Estado de badge na lista, derivado direto de `Campaign.status` (estado explícito de domínio
 * desde `add-campaign-pendente-state`). Único caso heurístico remanescente: `finalizada` ocupa a
 * janela transitória entre items terminais e `maybeFinalizeCampaignIfAllTerminal` setar `encerrada`.
 */
export function campaignListDisplayKind(
  campaign: Campaign,
  itemsForCampaign: CampaignItem[],
  _primaryDisparo: Disparo | null | undefined
): CampaignListDisplayKind {
  if (campaign.status === 'rascunho') return 'rascunho';
  if (campaign.status === 'encerrada') return 'encerrada';
  if (campaign.status === 'pendente') return 'pendente';

  // ativa: janela transitória "tudo terminal mas Finalizar manual ainda não foi clicado"
  const n = itemsForCampaign.length;
  const allTerminal =
    n > 0 && itemsForCampaign.every((i) => CAMPAIGN_TERMINAL_STAGES.includes(i.pipelineStage));
  if (allTerminal) return 'finalizada';

  return 'em_execucao';
}

export function campaignListDisplayBadge(kind: CampaignListDisplayKind): { label: string; className: string } {
  const map: Record<CampaignListDisplayKind, { label: string; className: string }> = {
    rascunho: { label: 'Rascunho', className: 'status-rascunho' },
    encerrada: { label: 'Encerrada', className: 'status-encerrada' },
    pendente: { label: 'Aguardando início', className: 'status-pendente' },
    em_execucao: { label: 'Em execução', className: 'status-em-execucao' },
    finalizada: { label: 'Finalizada', className: 'status-finalizada' },
  };
  return map[kind];
}
